"use client";
import { useRef } from "react";
import { motion, useInView, Variants } from "framer-motion";
import { Check } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import RichText from "./RichText";

export default function TargetAudience() {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-20px" });
  const { t, tv } = useLanguage();

  const checklist = tv<string[]>("audience.checklist");
  /* The four cards each carry a hand-drawn SVG below, so they're written out
     one by one rather than mapped — only the copy is looked up. */
  const cards = tv<{ title: string; desc: string }[]>("audience.cards");

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.12, delayChildren: 0.2 }
    }
  };

  const cardVariants: Variants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { type: "spring", stiffness: 100, damping: 15 }
    }
  };

  return (
    <section ref={ref} className="w-full py-12 sm:py-16 sm:py-20 lg:py-28 bg-gradient-to-br from-[#EFF3FE] via-[#FAFCFF] to-[#EFF3FE] overflow-hidden relative">
      {/* Background Dotted Network Pattern */}
      <div className="absolute inset-0 opacity-[0.025] pointer-events-none z-0" 
        style={{ backgroundImage: 'radial-gradient(#3D5EE1 1.5px, transparent 1.5px)', backgroundSize: '40px 40px' }} />

      {/* Soft background ambient blur */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#3D5EE1]/[0.04] rounded-full blur-[120px] pointer-events-none translate-x-1/3 -translate-y-1/3" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#3D5EE1]/0.05 rounded-full blur-[100px] pointer-events-none -translate-x-1/3 translate-y-1/3" />

      {/* Bottom Decorative Wavy Landscape */}
      <div className="absolute bottom-0 left-0 w-full pointer-events-none z-0 opacity-40">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
          <path d="M0,64L60,58.7C120,53,240,43,360,48C480,53,600,75,720,80C840,85,960,75,1080,64C1200,53,1320,43,1380,37.3L1440,32L1440,120L1380,120C1320,120,1200,120,1080,120C960,120,840,120,720,120C600,120,480,120,360,120C240,120,120,120,60,120L0,120Z" fill="#D6E0FC" />
        </svg>
      </div>

      <div className="site-container relative z-10">
        <div className="flex flex-col lg:flex-row gap-10 sm:gap-12 lg:gap-16 items-start">

          {/* ── Left Column ── */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="w-full lg:w-[42%] pt-4"
          >
            <h2 className="font-heading section-heading text-[#0d1117] mb-6 text-center lg:text-left">
              <RichText text={t("audience.heading")} />
            </h2>

            <p className="text-[15px] text-[#596875] font-medium leading-relaxed mb-10 max-w-[440px] mx-auto lg:mx-0 text-center lg:text-left">
              {t("audience.body")}
            </p>

            <div className="grid grid-cols-2 lg:grid-cols-1 gap-y-3 sm:gap-y-4 gap-x-6 sm:gap-x-12 w-fit mx-auto lg:mx-0">
              {checklist.map((item, i) => (
                <motion.div 
                  key={i} 
                  whileHover={{ x: 6 }}
                  whileTap={{ x: 6 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className="flex items-center gap-2.5 sm:gap-3.5 cursor-default group"
                >
                  <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#3D5EE1] group-hover:bg-[#172B88] group-active:bg-[#172B88] flex items-center justify-center flex-shrink-0 shadow-sm transition-[transform,background-color] duration-300 group-hover:scale-110 group-active:scale-110">
                    <Check size={12} strokeWidth={3} className="text-white sm:w-[14px] sm:h-[14px]" />
                  </div>
                  <span className="text-[12.5px] min-[380px]:text-[13.5px] sm:text-[15px] text-[#0d1117] group-hover:text-[#3D5EE1] group-active:text-[#3D5EE1] transition-colors leading-tight">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* ── Right Column (2x2 Cards Grid with Stagger & Spring Hover) ── */}
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="lg:w-[58%] grid grid-cols-2 gap-3 sm:gap-6 w-full"
          >
            
            {/* Card 1: Private Schools */}
            <motion.div 
              variants={cardVariants}
              whileHover={{ y: -8, scale: 1.02 }}
              whileTap={{ y: -8, scale: 1.02 }}
              className="lp-glow-card bg-white p-4 sm:p-7 pb-20 sm:pb-24 rounded-[20px] sm:rounded-[28px] border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_22px_45px_-12px_rgba(61,94,225,0.35)] hover:border-transparent active:shadow-[0_22px_45px_-12px_rgba(61,94,225,0.35)] active:border-transparent transition-[box-shadow,border-color,background-color] duration-300 relative overflow-hidden group cursor-default"
            >
              <h3 className="font-bold text-[14px] sm:text-[18px] text-[#0d1117] mb-1.5 sm:mb-3 group-hover:text-[#3D5EE1] group-active:text-[#3D5EE1] transition-colors leading-tight">{cards[0].title}</h3>
              <p className="text-[11.5px] sm:text-[13.5px] text-[#596875] leading-snug sm:leading-relaxed font-medium max-w-[220px]">
                {cards[0].desc}
              </p>
              
              {/* Illustration: School Building */}
              <svg viewBox="0 0 140 100" className="w-[85px] sm:w-[125px] h-auto absolute bottom-0 right-0 sm:right-2 pointer-events-none group-hover:scale-110 group-hover:-translate-y-1 group-active:scale-110 group-active:-translate-y-1 transition-transform duration-500 ease-out" fill="none" xmlns="http://www.w3.org/2000/svg">
                <ellipse cx="70" cy="94" rx="60" ry="5" fill="#2A48C9" opacity="0.1" />
                <path d="M18 90 L18 72" stroke="#2A48C9" strokeWidth="3" />
                <circle cx="18" cy="62" r="13" fill="#93C5FD" />
                <circle cx="12" cy="68" r="9" fill="#3D5EE1" />
                <path d="M122 90 L122 72" stroke="#2A48C9" strokeWidth="3" />
                <circle cx="122" cy="62" r="13" fill="#93C5FD" />
                <circle cx="128" cy="68" r="9" fill="#3D5EE1" />
                <rect x="36" y="38" width="68" height="54" fill="#EFF3FE" stroke="#3D5EE1" strokeWidth="2.5" rx="2" />
                <polygon points="36,38 70,18 104,38" fill="#3D5EE1" stroke="#2A48C9" strokeWidth="2.5" />
                <rect x="62" y="10" width="16" height="15" fill="#D6E0FC" stroke="#3D5EE1" strokeWidth="2.5" />
                <polygon points="62,10 70,2 78,10" fill="#F59E0B" />
                <path d="M70 2 L70 -5 M70 -5 L80 -1 L70 3" fill="#EF4444" stroke="#EF4444" strokeWidth="2" />
                <path d="M61 92 L61 68 C61 62, 79 62, 79 68 L79 92 Z" fill="#2563EB" stroke="#1E40AF" strokeWidth="2" />
                <rect x="45" y="48" width="10" height="12" fill="#93C5FD" stroke="#1E40AF" strokeWidth="1.5" rx="1" />
                <rect x="85" y="48" width="10" height="12" fill="#93C5FD" stroke="#1E40AF" strokeWidth="1.5" rx="1" />
                <rect x="45" y="68" width="10" height="12" fill="#93C5FD" stroke="#1E40AF" strokeWidth="1.5" rx="1" />
                <rect x="85" y="68" width="10" height="12" fill="#93C5FD" stroke="#1E40AF" strokeWidth="1.5" rx="1" />
              </svg>
            </motion.div>

            {/* Card 2: CBSE / ICSE */}
            <motion.div 
              variants={cardVariants}
              whileHover={{ y: -8, scale: 1.02 }}
              whileTap={{ y: -8, scale: 1.02 }}
              className="lp-glow-card bg-white p-4 sm:p-7 pb-20 sm:pb-24 rounded-[20px] sm:rounded-[28px] border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_22px_45px_-12px_rgba(61,94,225,0.35)] hover:border-transparent active:shadow-[0_22px_45px_-12px_rgba(61,94,225,0.35)] active:border-transparent transition-[box-shadow,border-color,background-color] duration-300 relative overflow-hidden group cursor-default"
            >
              <h3 className="font-bold text-[14px] sm:text-[18px] text-[#0d1117] mb-1.5 sm:mb-3 group-hover:text-[#3D5EE1] group-active:text-[#3D5EE1] transition-colors leading-tight">{cards[1].title}</h3>
              <p className="text-[11.5px] sm:text-[13.5px] text-[#596875] leading-snug sm:leading-relaxed font-medium max-w-[220px]">
                {cards[1].desc}
              </p>
              
              {/* Illustration: Graduation Cap & Books */}
              <svg viewBox="0 0 140 110" className="w-[85px] sm:w-[125px] h-auto absolute bottom-0 right-1 sm:right-3 pointer-events-none group-hover:scale-110 group-hover:-translate-y-1 group-active:scale-110 group-active:-translate-y-1 transition-transform duration-500 ease-out" fill="none" xmlns="http://www.w3.org/2000/svg">
                <ellipse cx="70" cy="102" rx="55" ry="6" fill="#2A48C9" opacity="0.1" />
                <path d="M30 85 L115 68 L125 78 L40 95 Z" fill="#F59E0B" stroke="#D97706" strokeWidth="2.5" />
                <path d="M30 85 L40 95 L40 99 L30 89 Z" fill="#B45309" stroke="#D97706" strokeWidth="2" />
                <path d="M115 68 L125 78 L125 82 L115 72 Z" fill="#FDE68A" stroke="#D97706" strokeWidth="2" />
                <path d="M25 73 L110 56 L120 66 L35 83 Z" fill="#3B82F6" stroke="#1D4ED8" strokeWidth="2.5" />
                <path d="M25 73 L35 83 L35 87 L25 77 Z" fill="#1E40AF" stroke="#1D4ED8" strokeWidth="2" />
                <path d="M110 56 L120 66 L120 70 L110 60 Z" fill="#BFDBFE" stroke="#1D4ED8" strokeWidth="2" />
                <path d="M20 61 L105 44 L115 54 L30 71 Z" fill="#60A5FA" stroke="#2563EB" strokeWidth="2.5" />
                <path d="M20 61 L30 71 L30 75 L20 65 Z" fill="#1D4ED8" stroke="#2563EB" strokeWidth="2" />
                <path d="M105 44 L115 54 L115 58 L105 48 Z" fill="#E0F2FE" stroke="#2563EB" strokeWidth="2" />
                <polygon points="70,12 120,30 70,48 20,30" fill="#1E293B" stroke="#0F172A" strokeWidth="2.5" />
                <path d="M45 39 L45 52 C45 58, 95 58, 95 52 L95 39" fill="#334155" stroke="#0F172A" strokeWidth="2.5" />
                <path d="M102 30 L108 55" stroke="#F59E0B" strokeWidth="3" strokeLinecap="round" />
                <circle cx="109" cy="59" r="4" fill="#F59E0B" />
                <path d="M107 59 L111 68 M109 59 L109 70 L111 59 L107 68" stroke="#D97706" strokeWidth="2" />
              </svg>
            </motion.div>

            {/* Card 3: K-12 & Play-Schools */}
            <motion.div 
              variants={cardVariants}
              whileHover={{ y: -8, scale: 1.02 }}
              whileTap={{ y: -8, scale: 1.02 }}
              className="lp-glow-card bg-white p-4 sm:p-7 pb-20 sm:pb-24 rounded-[20px] sm:rounded-[28px] border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_22px_45px_-12px_rgba(61,94,225,0.35)] hover:border-transparent active:shadow-[0_22px_45px_-12px_rgba(61,94,225,0.35)] active:border-transparent transition-[box-shadow,border-color,background-color] duration-300 relative overflow-hidden group cursor-default"
            >
              <h3 className="font-bold text-[14px] sm:text-[18px] text-[#0d1117] mb-1.5 sm:mb-3 group-hover:text-[#3D5EE1] group-active:text-[#3D5EE1] transition-colors leading-tight">{cards[2].title}</h3>
              <p className="text-[11.5px] sm:text-[13.5px] text-[#596875] leading-snug sm:leading-relaxed font-medium max-w-[220px]">
                {cards[2].desc}
              </p>
              
              {/* Illustration: Alphabet Blocks & Pencil */}
              <svg viewBox="0 0 140 100" className="w-[85px] sm:w-[125px] h-auto absolute bottom-0 right-0 sm:right-2 pointer-events-none group-hover:scale-110 group-hover:-translate-y-1 group-active:scale-110 group-active:-translate-y-1 transition-transform duration-500 ease-out" fill="none" xmlns="http://www.w3.org/2000/svg">
                <ellipse cx="70" cy="94" rx="55" ry="5" fill="#2A48C9" opacity="0.1" />
                <rect x="25" y="48" width="34" height="34" fill="#3B82F6" stroke="#1D4ED8" strokeWidth="2.5" rx="4" />
                <text x="42" y="73" fill="#FFFFFF" fontSize="22" fontWeight="bold" textAnchor="middle">A</text>
                <rect x="68" y="42" width="36" height="40" fill="#F59E0B" stroke="#D97706" strokeWidth="2.5" rx="4" />
                <text x="86" y="70" fill="#FFFFFF" fontSize="22" fontWeight="bold" textAnchor="middle">B</text>
                <path d="M20 38 L115 15 L125 24 L30 47 Z" fill="#10B981" stroke="#047857" strokeWidth="2" />
                <polygon points="20,38 12,40 17,33" fill="#FDE68A" stroke="#D97706" strokeWidth="1.5" />
              </svg>
            </motion.div>

            {/* Card 4: Tier 2 & 3 Cities */}
            <motion.div 
              variants={cardVariants}
              whileHover={{ y: -8, scale: 1.02 }}
              whileTap={{ y: -8, scale: 1.02 }}
              className="lp-glow-card bg-white p-4 sm:p-7 pb-20 sm:pb-24 rounded-[20px] sm:rounded-[28px] border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_22px_45px_-12px_rgba(61,94,225,0.35)] hover:border-transparent active:shadow-[0_22px_45px_-12px_rgba(61,94,225,0.35)] active:border-transparent transition-[box-shadow,border-color,background-color] duration-300 relative overflow-hidden group cursor-default"
            >
              <h3 className="font-bold text-[14px] sm:text-[18px] text-[#0d1117] mb-1.5 sm:mb-3 group-hover:text-[#3D5EE1] group-active:text-[#3D5EE1] transition-colors leading-tight">{cards[3].title}</h3>
              <p className="text-[11.5px] sm:text-[13.5px] text-[#596875] leading-snug sm:leading-relaxed font-medium max-w-[220px]">
                {cards[3].desc}
              </p>
              
              {/* Illustration: City Skyline */}
              <svg viewBox="0 0 140 100" className="w-[85px] sm:w-[125px] h-auto absolute bottom-0 right-0 sm:right-2 pointer-events-none group-hover:scale-110 group-hover:-translate-y-1 group-active:scale-110 group-active:-translate-y-1 transition-transform duration-500 ease-out" fill="none" xmlns="http://www.w3.org/2000/svg">
                <ellipse cx="70" cy="94" rx="60" ry="5" fill="#2A48C9" opacity="0.1" />
                <rect x="12" y="38" width="22" height="54" fill="#E0F2FE" stroke="#3B82F6" strokeWidth="2" rx="1" />
                <rect x="75" y="45" width="22" height="47" fill="#E0F2FE" stroke="#3B82F6" strokeWidth="2" rx="1" />
                <rect x="38" y="18" width="32" height="74" fill="#EFF3FE" stroke="#3D5EE1" strokeWidth="2.5" rx="2" />
                <polygon points="38,18 54,4 70,18" fill="#3D5EE1" stroke="#2A48C9" strokeWidth="2" />
                <rect x="98" y="28" width="26" height="64" fill="#D6E0FC" stroke="#3D5EE1" strokeWidth="2.5" rx="2" />
                <polygon points="98,28 111,14 124,28" fill="#93C5FD" stroke="#3D5EE1" strokeWidth="2" />
                <rect x="45" y="28" width="6" height="8" fill="#93C5FD" rx="1" />
                <rect x="57" y="28" width="6" height="8" fill="#93C5FD" rx="1" />
                <rect x="45" y="42" width="6" height="8" fill="#93C5FD" rx="1" />
                <rect x="57" y="42" width="6" height="8" fill="#93C5FD" rx="1" />
                <rect x="45" y="56" width="6" height="8" fill="#93C5FD" rx="1" />
                <rect x="57" y="56" width="6" height="8" fill="#93C5FD" rx="1" />
                <rect x="45" y="70" width="6" height="8" fill="#93C5FD" rx="1" />
                <rect x="57" y="70" width="6" height="8" fill="#93C5FD" rx="1" />
                <rect x="104" y="38" width="5" height="7" fill="#FFFFFF" rx="1" />
                <rect x="113" y="38" width="5" height="7" fill="#FFFFFF" rx="1" />
                <rect x="104" y="52" width="5" height="7" fill="#FFFFFF" rx="1" />
                <rect x="113" y="52" width="5" height="7" fill="#FFFFFF" rx="1" />
                <rect x="104" y="66" width="5" height="7" fill="#FFFFFF" rx="1" />
                <rect x="113" y="66" width="5" height="7" fill="#FFFFFF" rx="1" />
                <rect x="18" y="48" width="10" height="6" fill="#93C5FD" rx="1" />
                <rect x="18" y="60" width="10" height="6" fill="#93C5FD" rx="1" />
              </svg>
            </motion.div>

          </motion.div>
        </div>
      </div>
    </section>
  );
}

